import { Card, CardContent, CardMedia } from "components/Card"
import { Link } from "components/Link"
import { LogoFull } from "components/Logo"
import { Text } from "components/Text"
import * as WidgetLayout from "enums/WidgetLayout"
import * as AdvertType from "enums/AdvertType"
import { Image } from "components/Image"
import { AdvertDate } from "domains/advert/AdvertDate"
import { UserAvatar } from "domains/user/UserAvatar"
import cx from "classnames"
import { BoatPlaceholder } from "components/Icon/BoatPlaceholder"
import { useRouter } from "components/Router"
import { RawLink } from "components/Link"
import { Carousel } from "components/Carousel"

export function WidgetShowPage({ widget, adverts }) {
  const Component = getComponentForWidgetLayout(widget.layout.enum_value)
  WidgetShowPage.registrationOrigin = widget.registrationOrigin?.slug

  if (!Component) {
    return null
  }

  return <Component widget={widget} adverts={adverts} />
}

WidgetShowPage.hideZendesk = true

const getComponentForWidgetLayout = (widgetLayout) => {
  const widgetLayoutToComponent = {
    [WidgetLayout.W300_H531]: W300_H531,
    [WidgetLayout.W300_H400]: W300_H400,
    [WidgetLayout.W1260_H256]: W1260_H256,
  }

  return widgetLayoutToComponent[widgetLayout]
}

const DisplayLogo = () => {
  const router = useRouter()

  return (
    <div className="shrink-0">
      <RawLink
        href={router.generate(
          "app_home_index",
          WidgetShowPage.registrationOrigin
            ? { origin: WidgetShowPage.registrationOrigin }
            : null,
          true,
        )}
        external
      >
        <LogoFull width="100" />
      </RawLink>
    </div>
  )
}

/**
 * @typedef {Object} WidgetRendererProps
 * @prop {Object} widget
 * @prop {Array<object>} adverts
 */

/** @typedef {(props: WidgetRendererProps) => import("react").ReactElement} WidgetRenderer */

/** @type WidgetRenderer */
const W300_H531 = ({ widget, adverts }) => {
  return (
    <div className={"w-[300px] h-[531px]"}>
      <VerticalWidget adverts={adverts} widget={widget} cardSize={"large"} />
    </div>
  )
}

/**
 * @param {Object} props
 * @param {Array<object>} props.adverts
 * @param {AdvertCardProps["size"]} cardSize
 */
const AdvertsCarousel = ({ adverts, cardSize }) => {
  return (
    <Carousel
      slides={adverts.map((advert) => (
        <AdvertCard key={advert.id} advert={advert} size={cardSize} />
      ))}
      interval={5000}
      autoStart={true}
      components={{
        ActiveSlideIndicator: () => null,
        NextButton: () => null,
        PreviousButton: () => null,
        BulletsContainer,
        Bullet,
      }}
    />
  )
}

/** @typedef {import("components/Carousel").CarouselComponents} CarouselComponents */

/** @type {CarouselComponents["BulletsContainer"]} */
const BulletsContainer = ({ children }) => {
  return <div className={"flex justify-center gap-2 mt-5"}>{children}</div>
}

/** @type {CarouselComponents["Bullet"]} */
const Bullet = ({ onClick, isActive }) => {
  return (
    <button
      type={"button"}
      onClick={onClick}
      className={cx(
        "rounded-full w-4 h-4 bg-primary-default transition-transform duration-150",
        { "scale-50": !isActive, "scale-100": isActive },
      )}
    />
  )
}

const AdvertsList = ({ adverts, cardSize }) => {
  return (
    <div className={"space-y-1"}>
      {adverts.map((advert) => (
        <AdvertCard key={advert.id} advert={advert} size={cardSize} />
      ))}
    </div>
  )
}

/**
 * @param {Object} props
 * @param {Array<object>} props.children
 * @param {Object} props.widget
 * @param {AdvertCardProps["size"]} cardSize
 */
const VerticalWidget = ({ adverts, widget, cardSize }) => {
  const [advert] = adverts

  const Component =
    advert?.type.enum_value === "crew" ? AdvertsList : AdvertsCarousel

  return (
    <div className={"w-full h-full grid grid-rows-[auto_1fr_auto] gap-2"}>
      <header className="bg-white p-2 flex">
        <DisplayLogo widget={widget} />
        <Text
          variant="headline5"
          className="text-primary-dark uppercase text-center grow"
        >
          {widget.title}
        </Text>
      </header>

      <main className="px-2 space-y-2 min-w-0">
        <Component adverts={adverts} cardSize={cardSize} />
      </main>

      {widget.cta1 || widget.cta2 ? (
        <footer className="px-2">
          <div className="flex flex-col items-center">
            {widget.cta1 && widget.url1 ? (
              <Link
                href={
                  WidgetShowPage.registrationOrigin
                    ? widget.url1 +
                      (widget.url1.includes("?") ? "&" : "?") +
                      "origin=" +
                      WidgetShowPage.registrationOrigin
                    : widget.url1
                }
                external
              >
                {widget.cta1}
              </Link>
            ) : null}
            {widget.cta2 && widget.url2 ? (
              <Link
                href={
                  WidgetShowPage.registrationOrigin
                    ? widget.url2 +
                      (widget.url2.includes("?") ? "&" : "?") +
                      "origin=" +
                      WidgetShowPage.registrationOrigin
                    : widget.url2
                }
                external
              >
                {widget.cta2}
              </Link>
            ) : null}
          </div>
        </footer>
      ) : null}
    </div>
  )
}

/** @type {WidgetRenderer} */
const W300_H400 = ({ widget, adverts }) => {
  return (
    <div className={"w-[300px] h-[400px]"}>
      <VerticalWidget adverts={adverts} widget={widget} cardSize={"medium"} />
    </div>
  )
}

const W1260_H256 = ({ widget, adverts }) => {
  return (
    <div>
      <header className="bg-white p-2 flex items-center space-x-4">
        <DisplayLogo widget={widget} />
        <div className="grow">
          <Text variant="headline5" className="text-primary-dark uppercase">
            {widget.title}
          </Text>
        </div>
        {widget.cta1 || widget.cta2 ? (
          <div className="ml-auto">
            <div className="flex items-center space-x-2">
              {widget.cta1 && widget.url1 ? (
                <Link
                  href={
                    WidgetShowPage.registrationOrigin
                      ? widget.url1 +
                        (widget.url1.includes("?") ? "&" : "?") +
                        "origin=" +
                        WidgetShowPage.registrationOrigin
                      : widget.url1
                  }
                  external
                >
                  {widget.cta1}
                </Link>
              ) : null}
              {widget.cta2 && widget.url2 ? (
                <Link
                  href={
                    WidgetShowPage.registrationOrigin
                      ? widget.url2 +
                        (widget.url2.includes("?") ? "&" : "?") +
                        "origin=" +
                        WidgetShowPage.registrationOrigin
                      : widget.url2
                  }
                  external
                >
                  {widget.cta2}
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}
      </header>
      <main className="px-2 py-1 grid grid-cols-4 gap-4">
        {adverts.map((advert) => (
          <AdvertCard key={advert.id} advert={advert} size="medium" />
        ))}
      </main>
    </div>
  )
}

/**
 * @typedef {Object} AdvertCardProps
 * @prop {Object} advert
 * @prop {"small" | "medium" | "large"} size
 */

/**
 * @param {AdvertCardProps} props
 */
const AdvertCard = ({ advert, size }) => {
  const Component = getAdvertCardComponent(advert)
  if (!Component) {
    return null
  }

  return <Component advert={advert} size={size} />
}

const getAdvertCardComponent = (advert) => {
  const advertTypeToComponent = {
    [AdvertType.BOAT]: BoatAdvert,
    [AdvertType.CREW]: CrewAdvert,
    [AdvertType.SALE]: BoatAdvert,
    [AdvertType.FLEET]: BoatAdvert,
    [AdvertType.RENTAL]: BoatAdvert,
  }

  return advertTypeToComponent[advert.type.enum_value]
}

const BoatAdvert = ({ advert, size }) => {
  const image = advert.imageFile || advert.boat?.imageFile
  const router = useRouter()

  return (
    <div>
      <RawLink
        href={router.generate(
          "app_account_advert_show",
          WidgetShowPage.registrationOrigin
            ? {
                advertId: advert.id,
                userId: advert.user.id,
                origin: WidgetShowPage.registrationOrigin,
              }
            : {
                advertId: advert.id,
                userId: advert.user.id,
              },
          true,
        )}
        external
      >
        <Card spacing="small">
          {image ? (
            <CardMedia
              media={
                <Image imageFile={image} size="w400" className="object-cover" />
              }
              className={cx({
                "h-16": size === "small",
                "h-36": size === "medium",
                "h-40": size === "large",
              })}
            />
          ) : (
            <div
              className={cx(
                {
                  "h-16": size === "small",
                  "h-24": size === "medium",
                  "h-40": size === "large",
                },
                "bg-grey-light",
                "items-center",
                "justify-center",
                "flex",
              )}
            >
              <BoatPlaceholder
                className={cx({
                  "w-4": size === "small",
                  "w-6": size === "medium",
                  "w-12": size === "large",
                })}
              />
            </div>
          )}
          <CardContent>
            <AdvertDate advert={advert} />
            <Text
              variant="headline5"
              className={cx({
                "line-clamp-1": size === "small",
                "line-clamp-2": "medium",
                "line-clamp-4": size === "large",
              })}
            >
              {advert.title}
            </Text>
            {size === "large" ? (
              <Text variant="caption" className={"line-clamp-2"}>
                {advert.description}
              </Text>
            ) : null}
          </CardContent>
        </Card>
      </RawLink>
    </div>
  )
}

const CrewAdvert = ({ advert, size }) => {
  const router = useRouter()
  return (
    <div>
      <RawLink
        href={router.generate(
          "app_account_advert_show",
          WidgetShowPage.registrationOrigin
            ? {
                advertId: advert.id,
                userId: advert.user.id,
                origin: WidgetShowPage.registrationOrigin,
              }
            : {
                advertId: advert.id,
                userId: advert.user.id,
              },
          true,
        )}
        external
      >
        <Card>
          <div className="flex items-center overflow-hidden">
            <div className="shrink-0 w-20 px-4 py-3 bg-primary-lighter">
              <UserAvatar
                user={advert.user}
                size={size === "small" ? "xsmall" : "small"}
                showName
              />
            </div>
            <div className="grow p-2">
              <AdvertDate advert={advert} />
              <Text variant="headline6" className="line-clamp-3">
                {advert.title}
              </Text>
            </div>
          </div>
        </Card>
      </RawLink>
    </div>
  )
}
